import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Tabs, Tab } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { projects } from '../data/projectsData';
import Icon from '../components/common/Icon';
import './ProjectDetailsPage.css';

// A lightweight, premium Markdown parsing component to render project READMEs beautifully
interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const parseMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeContent: string[] = [];
    let codeLanguage = '';
    let inList = false;
    let listItems: string[] = [];

    const flushList = (key: number) => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${key}`} className="readme-list ps-4 mb-4">
            {listItems.map((item, i) => (
              <li key={i} className="readme-list-item mb-2" style={{ listStyleType: 'disc', color: 'var(--text-color)' }}>
                {parseInlineFormatting(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    const parseInlineFormatting = (str: string): React.ReactNode[] => {
      const regex = /(\*\*.*?\*\*|`.*?`)/g;
      const splitParts = str.split(regex);
      
      return splitParts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-weight-bold" style={{ color: 'var(--heading-color)' }}>{part.slice(2, -2)}</strong>;
        } else if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code 
              key={i} 
              className="readme-inline-code px-2 py-0.5 rounded" 
              style={{ 
                background: 'rgba(99, 102, 241, 0.1)', 
                color: 'var(--accent-color, #6366f1)',
                fontFamily: 'monospace', 
                fontSize: '0.85em',
                border: '1px solid rgba(99, 102, 241, 0.2)'
              }}
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        return part;
      });
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          // End of code block
          elements.push(
            <pre key={`code-${i}`} className="readme-code-block p-3 rounded mb-4" style={{ background: 'rgba(7, 10, 19, 0.8)', border: '1px solid var(--border-color)', overflowX: 'auto', fontFamily: 'monospace' }}>
              <code className={`language-${codeLanguage}`} style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>
                {codeContent.join('\n')}
              </code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          // Start of code block
          flushList(i);
          inCodeBlock = true;
          codeLanguage = line.trim().slice(3);
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      // Headings
      if (line.startsWith('# ')) {
        flushList(i);
        elements.push(<h1 key={i} className="readme-h1 font-display border-bottom pb-2 mb-4 mt-3" style={{ fontSize: '1.8rem', borderBottomColor: 'var(--border-color)', color: 'var(--heading-color)' }}>{parseInlineFormatting(line.slice(2))}</h1>);
      } else if (line.startsWith('## ')) {
        flushList(i);
        elements.push(<h2 key={i} className="readme-h2 font-display mb-3 mt-4" style={{ fontSize: '1.4rem', color: 'var(--heading-color)' }}>{parseInlineFormatting(line.slice(3))}</h2>);
      } else if (line.startsWith('### ')) {
        flushList(i);
        elements.push(<h3 key={i} className="readme-h3 font-display mb-3 mt-3" style={{ fontSize: '1.15rem', color: 'var(--heading-color)' }}>{parseInlineFormatting(line.slice(4))}</h3>);
      }
      // Blockquotes / Alerts
      else if (line.trim().startsWith('>')) {
        flushList(i);
        const quoteContent = line.trim().slice(1).trim();
        elements.push(
          <blockquote key={i} className="readme-blockquote border-start border-primary border-4 ps-3 py-2 my-3 rounded-end" style={{ background: 'rgba(var(--accent-rgb), 0.04)', borderColor: 'var(--accent-color)' }}>
            <p className="mb-0" style={{ fontStyle: 'italic', color: 'var(--text-color)' }}>{parseInlineFormatting(quoteContent)}</p>
          </blockquote>
        );
      }
      // Lists (Unordered)
      else if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        inList = true;
        const cleanItem = line.trim().slice(2);
        listItems.push(cleanItem);
      }
      // Numbered Lists
      else if (/^\d+\.\s/.test(line.trim())) {
        inList = true;
        const cleanItem = line.trim().replace(/^\d+\.\s/, '');
        listItems.push(cleanItem);
      }
      // Empty lines
      else if (line.trim() === '') {
        if (inList) {
          flushList(i);
        }
      }
      // Paragraphs
      else {
        flushList(i);
        elements.push(
          <p key={i} className="readme-paragraph leading-relaxed mb-3" style={{ fontSize: '0.98rem', color: 'var(--text-color)' }}>
            {parseInlineFormatting(line)}
          </p>
        );
      }
    }

    flushList(lines.length);

    return elements;
  };

  return <div className="readme-content-rendered">{parseMarkdown(content)}</div>;
};

const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const projectId = parseInt(id || '1', 10);
  const projectIndex = projects.findIndex(p => p.id === projectId);
  const project = projects[projectIndex];

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <section className="project-details-not-found py-5">
        <Container className="text-center py-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="glass-panel p-5 max-width-600 mx-auto"
          >
            <h2 className="mb-4">Project Not Found</h2>
            <p className="text-secondary mb-4">The project you are looking for does not exist or has been moved.</p>
            <Link to="/">
              <Button variant="primary">Return Home</Button>
            </Link>
          </motion.div>
        </Container>
      </section>
    );
  }

  // Next and Previous projects dynamic indexing
  const prevProject = projects[(projectIndex - 1 + projects.length) % projects.length];
  const nextProject = projects[(projectIndex + 1) % projects.length];

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] 
      }
    }
  };

  return (
    <motion.section 
      className="project-details-section"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="radial-glow-details" />
      <Container>
        {/* Navigation Breadcrumbs & Back Button */}
        <div className="details-header-nav d-flex justify-content-between align-items-center mb-4">
          <Link to="/projects" className="back-btn-pill d-flex align-items-center text-decoration-none">
            <span className="back-pill-arrow me-2">←</span> Back to Projects
          </Link>
          <div className="details-breadcrumbs text-muted font-weight-medium">
            <Link to="/">Home</Link> / <Link to="/projects">Projects</Link> / <span className="active-breadcrumb text-secondary">{project.title}</span>
          </div>
        </div>

        {/* Project Header Banner */}
        <Row className="mb-5 align-items-end">
          <Col lg={8} md={12}>
            <div className="project-details-meta">
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <span className="project-category-tag mb-0">
                  {project.category.join(' & ')}
                </span>
                {project.hackathon && (
                  <span className="badge bg-danger text-white px-3 py-1.5 rounded-pill shadow-sm" style={{ fontWeight: 700, letterSpacing: '0.5px', background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', fontSize: '0.78rem' }}>
                    ⚡ Hackathon Project
                  </span>
                )}
              </div>
              <h1 className="project-details-title font-display mb-3">{project.title}</h1>
              <p className="project-details-subtitle text-secondary mb-0">{project.subtitle}</p>
            </div>
          </Col>
          <Col lg={4} md={12} className="text-lg-end mt-4 mt-lg-0">
            <div className="project-quick-actions d-flex gap-2 justify-content-lg-end flex-wrap">
              {project.codeLink && (
                <Button 
                  variant="outline-primary"
                  href={project.codeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center"
                >
                  <Icon icon="FaGithub" className="me-2" /> Code
                </Button>
              )}
              {project.reportLink && (
                <Button 
                  variant="outline-info"
                  href={project.reportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center report-btn"
                  style={{
                    borderColor: 'var(--cyan-accent, #06b6d4)',
                    color: 'var(--cyan-accent, #06b6d4)',
                  }}
                >
                  <Icon icon="FaFilePdf" className="me-2" /> Project Report
                </Button>
              )}
              {project.demoLink && (
                <Button 
                  variant="primary"
                  href={project.demoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center"
                >
                  <Icon icon="FaExternalLinkAlt" className="me-2" /> Live Demo
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Project Showcase Mockup Display */}
        <Row className="mb-5">
          <Col xs={12}>
            <motion.div 
              className="showcase-mockup-frame overflow-hidden position-relative"
              whileHover={{ scale: 1.005 }}
              transition={{ duration: 0.3 }}
            >
              {/* Window Controls Bar */}
              <div className="showcase-window-bar d-flex align-items-center px-3 py-2">
                <div className="window-dots d-flex gap-1.5 me-3">
                  <span className="window-dot red" />
                  <span className="window-dot yellow" />
                  <span className="window-dot green" />
                </div>
                <div className="window-address-bar text-muted text-center flex-grow-1">
                  {project.title.toLowerCase().replace(/\s+/g, '-')}.rebelhere.app
                </div>
              </div>
              
              {/* Main Media Showcase */}
              <div className="showcase-media-container position-relative">
                {project.videoAsset ? (
                  <video 
                    src={project.videoAsset} 
                    className="showcase-video w-100" 
                    controls 
                    autoPlay 
                    muted 
                    loop 
                    style={{ maxHeight: '480px', objectFit: 'contain', backgroundColor: '#020617' }}
                  />
                ) : (
                  <>
                    <img src={project.image} alt={project.title} className="showcase-image w-100" />
                    <div className="showcase-overlay-shadow" />
                  </>
                )}
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Detailed Tabs Interface (README, Engineering, Technical Tech) */}
        <Row className="mb-5">
          <Col lg={8} md={12} className="mb-4 mb-lg-0">
            <div className="details-tabs-container p-4 glass-panel">
              <Tabs defaultActiveKey="readme" id="project-tabs" className="custom-tabs mb-4">
                
                {/* TAB 1: README OVERVIEW */}
                <Tab eventKey="readme" title="📄 README.md">
                  <div className="tab-content-wrapper py-2">
                    {project.readmeContent ? (
                      <MarkdownRenderer content={project.readmeContent} />
                    ) : (
                      <>
                        <h3 className="tab-section-heading mb-3">Project Overview</h3>
                        <p className="tab-paragraph text-secondary mb-4 leading-relaxed">{project.fullDescription}</p>
                        
                        <h3 className="tab-section-heading mb-3">Core Product Features</h3>
                        <ul className="features-list-grid p-0 mb-0">
                          {project.features.map((feature, index) => (
                            <motion.li 
                              key={index} 
                              className="feature-list-item d-flex align-items-start mb-3"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                            >
                              <span className="feature-check-icon me-3">✓</span>
                              <span className="text-secondary">{feature}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </Tab>

                {/* TAB 2: ENGINEERING PROCESS */}
                <Tab eventKey="engineering" title="🛠️ Architecture & Challenges">
                  <div className="tab-content-wrapper py-2">
                    <h3 className="tab-section-heading mb-3">Technical Architecture</h3>
                    <p className="tab-paragraph text-secondary mb-4 leading-relaxed">{project.architecture}</p>
                    
                    <h3 className="tab-section-heading mb-3">Engineering Challenges & Solutions</h3>
                    <div className="challenge-solution-box p-3 rounded mb-0">
                      <div className="challenge-header d-flex align-items-center mb-2">
                        <span className="challenge-warning-icon me-2">⚠️</span>
                        <h5 className="mb-0 text-secondary font-weight-bold">The Challenge</h5>
                      </div>
                      <p className="text-secondary leading-relaxed mb-0">{project.challenges}</p>
                    </div>
                  </div>
                </Tab>

                {/* TAB 3: SPECIFIC TECHNOLOGIES */}
                <Tab eventKey="technologies" title="💻 Technology Stack">
                  <div className="tab-content-wrapper py-2">
                    <h3 className="tab-section-heading mb-3">Tech Stacks Selected</h3>
                    <p className="text-secondary mb-4">A complete mapping of libraries, platforms, and databases employed in the design of this project.</p>
                    
                    <div className="tech-tags-grid d-flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="tech-tag-large px-3 py-2 rounded">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </div>
          </Col>

          {/* Sidebar Specifications */}
          <Col lg={4} md={12}>
            <div className="project-details-sidebar glass-panel p-4">
              <h3 className="sidebar-heading font-display mb-4">Specifications</h3>
              
              <div className="sidebar-specs-list d-flex flex-column gap-3.5">
                <div className="sidebar-spec-item pb-3 border-bottom-dashed">
                  <span className="spec-label text-muted d-block mb-1">Developer Role</span>
                  <span className="spec-value font-weight-semibold">Lead Full-Stack Developer</span>
                </div>
                <div className="sidebar-spec-item pb-3 border-bottom-dashed">
                  <span className="spec-label text-muted d-block mb-1">Academic Affiliation</span>
                  <span className="spec-value font-weight-semibold">FAST-NUCES, Lahore</span>
                </div>
                <div className="sidebar-spec-item pb-3 border-bottom-dashed">
                  <span className="spec-label text-muted d-block mb-1">Primary Category</span>
                  <span className="spec-value font-weight-semibold text-capitalize">
                    {project.category.join(', ')}
                  </span>
                </div>
                <div className="sidebar-spec-item">
                  <span className="spec-label text-muted d-block mb-1">Version Control Status</span>
                  <span className="spec-value font-weight-semibold d-flex align-items-center">
                    <span className="status-dot green me-2" /> Production Ready
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Dynamic Project Index Navigation (Footer Arrow Bridges) */}
        <div className="project-details-footer-nav pt-5 mt-5 border-top">
          <Row className="g-4">
            <Col md={6} xs={12}>
              <Link to={`/project/${prevProject.id}`} className="footer-nav-card prev-card d-block p-4 text-decoration-none">
                <div className="d-flex align-items-center">
                  <div className="nav-icon-wrapper me-3 d-flex align-items-center justify-content-center">
                    <span className="nav-arrow">←</span>
                  </div>
                  <div className="nav-card-content text-start">
                    <span className="nav-card-label d-block text-uppercase">Previous Project</span>
                    <h5 className="nav-card-title mb-0 mt-1">{prevProject.title}</h5>
                  </div>
                </div>
              </Link>
            </Col>
            <Col md={6} xs={12}>
              <Link to={`/project/${nextProject.id}`} className="footer-nav-card next-card d-block p-4 text-decoration-none">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="nav-card-content text-end flex-grow-1 pe-3">
                    <span className="nav-card-label d-block text-uppercase">Next Project</span>
                    <h5 className="nav-card-title mb-0 mt-1">{nextProject.title}</h5>
                  </div>
                  <div className="nav-icon-wrapper d-flex align-items-center justify-content-center">
                    <span className="nav-arrow">→</span>
                  </div>
                </div>
              </Link>
            </Col>
          </Row>
        </div>
      </Container>
    </motion.section>
  );
};

export default ProjectDetailsPage;
